import React, { useState, useMemo } from 'react';
import { DollarSign, TrendingUp, AlertCircle, Info, Plus } from 'lucide-react';
import { Card } from '../ui/card';
import { ServiceCostList } from '../services/ServiceCostList';
import { DealServiceCostPreview } from './DealServiceCostPreview';
import { formatCurrency } from '../../lib/utils';
import { useDealServiceStore } from '../../lib/stores/dealServiceStore';
import { ResponsiveModal } from '../ui/ResponsiveModal';
import { ServiceCostForm } from '../services/ServiceCostForm';
import type { Deal } from '../../lib/types';

interface DealCostsSectionProps {
  deal: Deal;
  dealServices?: any[];
}

export function DealCostsSection({ deal, dealServices = [] }: DealCostsSectionProps) {
  const isAccepted = deal.status === 'accepted' || deal.status === 'completed';
  const [isFormOpen, setIsFormOpen] = useState(false);

  // Calculate total estimated costs from services
  const totalEstimatedCosts = deal.internal_cost_estimate || 0;
  const estimatedMargin = deal.estimated_margin_percentage || 0;

  // Calcola il valore dell'offerta escludendo marche da bollo e variabili esenti IVA
  const dealValue = useMemo(() => {
    let total = 0;
    dealServices.forEach(service => {
      total += service.discounted_amount || service.amount || 0;

      const variables = service.deal_variables || service.variables || [];
      variables.forEach((variable: any) => {
        // Include solo variabili confermate, NON esenti IVA e NON costi interni
        if (variable.is_confirmed && !variable.is_vat_exempt && !variable.is_cost) {
          if (variable.type === 'percentage' && variable.base_amount) {
            total += (variable.base_amount * variable.amount) / 100;
          } else {
            total += variable.amount || 0;
          }
        }
      });
    });
    return total;
  }, [dealServices]);

  return (
    <div className="space-y-3 md:space-y-4 px-3 md:px-4">
      {/* Subtitle only */}
      <p className="text-xs text-gray-600 dark:text-gray-400">
        {isAccepted
          ? 'Costi reali associati a questo contratto'
          : 'Stima costi interni basata sui template dei servizi'}
      </p>

      {/* Margin summary cards - glass background, no Card component */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 md:gap-3">
        <div className="p-2 md:p-3 rounded-lg bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-600 dark:text-gray-400 truncate">Valore</p>
              <p className="text-sm md:text-lg font-bold text-gray-900 dark:text-white truncate">
                {formatCurrency(dealValue)}
              </p>
            </div>
            <DollarSign className="hidden md:block h-6 w-6 text-blue-500 flex-shrink-0" />
          </div>
        </div>

        <div className="p-2 md:p-3 rounded-lg bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                {isAccepted ? 'Costi' : 'Costi St.'}
              </p>
              <p className="text-sm md:text-lg font-bold text-gray-900 dark:text-white truncate">
                {formatCurrency(totalEstimatedCosts)}
              </p>
            </div>
            <AlertCircle className="hidden md:block h-6 w-6 text-orange-500 flex-shrink-0" />
          </div>
        </div>

        <div className="p-2 md:p-3 rounded-lg bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                {isAccepted ? 'Margine' : 'Margine St.'}
              </p>
              <p className={`text-sm md:text-lg font-bold ${
                estimatedMargin >= 30 ? 'text-green-600 dark:text-green-400' :
                estimatedMargin >= 15 ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {estimatedMargin.toFixed(1)}%
              </p>
            </div>
            <TrendingUp className={`hidden md:block h-6 w-6 flex-shrink-0 ${
              estimatedMargin >= 30 ? 'text-green-500' :
              estimatedMargin >= 15 ? 'text-yellow-500' :
              'text-red-500'
            }`} />
          </div>
        </div>
      </div>

      {/* Content based on deal status */}
      {isAccepted ? (
        // Show actual costs for accepted deals
        <div className="space-y-3 md:space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
            <div className="flex items-start gap-2 p-2.5 md:p-3 bg-blue-50/50 dark:bg-blue-900/10 backdrop-blur-sm rounded-lg flex-1">
              <Info className="h-3.5 w-3.5 md:h-4 md:w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-blue-800 dark:text-blue-200">
                <strong>Contratto Accettato:</strong> <span className="hidden md:inline">I costi template sono stati convertiti in costi reali pronti per l'approvazione.</span><span className="md:hidden">Costi reali pronti.</span>
              </div>
            </div>
            <button
              onClick={() => setIsFormOpen(true)}
              className="flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 rounded-lg transition-colors min-h-[36px] md:min-h-0"
            >
              <Plus className="h-3.5 w-3.5" />
              <span className="md:inline">Aggiungi</span>
            </button>
          </div>

          <ServiceCostList
            dealId={deal.id}
            mode="actual"
            showActions={true}
            hideFilters={true}
          />

          {/* Modal for adding/editing costs */}
          <ResponsiveModal
            isOpen={isFormOpen}
            onClose={() => setIsFormOpen(false)}
            title="Nuovo Costo Effettivo"
          >
            <ServiceCostForm
              dealId={deal.id}
              mode="actual"
              onSave={() => {
                setIsFormOpen(false);
                // Refresh will happen automatically via store
              }}
              onCancel={() => setIsFormOpen(false)}
            />
          </ResponsiveModal>
        </div>
      ) : (
        // Show preview costs for draft deals
        <div className="space-y-3 md:space-y-4">
          <div className="flex items-start gap-2 p-2.5 md:p-3 bg-yellow-50/50 dark:bg-yellow-900/10 backdrop-blur-sm rounded-lg">
            <Info className="h-3.5 w-3.5 md:h-4 md:w-4 text-yellow-700 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-yellow-800 dark:text-yellow-200">
              <strong>Offerta in Bozza:</strong> <span className="hidden md:inline">Questi sono costi stimati basati sui template. Quando accetterai l'offerta, verranno creati automaticamente come costi reali da approvare.</span><span className="md:hidden">Costi stimati dai template.</span>
            </div>
          </div>

          {dealServices.length === 0 ? (
            <div className="text-center py-4 md:py-6 text-gray-500 dark:text-gray-400 text-xs md:text-sm">
              Nessun servizio aggiunto a questo contratto
            </div>
          ) : (
            <div className="space-y-2 md:space-y-3">
              {dealServices.map((dealService) => (
                dealService.service_id && (
                  <DealServiceCostPreview
                    key={dealService.id || dealService.service_id}
                    serviceId={dealService.service_id}
                    serviceName={dealService.service?.title || dealService.custom_service_name || 'Servizio'}
                  />
                )
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
